import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { 
  handleFailedAttempt, 
  isFormLocked, 
  resetAttempts,
  formatRemainingTime
} from '../utils/auth-utils';
import { useSecurity } from '../utils/SecurityContext';
import ReCAPTCHA from "react-google-recaptcha";

const Login = ({ user, setUser, getUser }) => {
  const mobile = useRef();
  const recaptchaRef = useRef();
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [formLock, setFormLock] = useState({ locked: false, remainingTime: 0 });
  const [timeDisplay, setTimeDisplay] = useState("");
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const navigate = useNavigate();
  
  // Utiliser le contexte de sécurité pour accéder au jeton CSRF et à l'instance axios sécurisée
  const { csrfToken, createSecureAxios } = useSecurity();
  const secureAxios = createSecureAxios();

  // Fonction pour mettre à jour le compteur de temps restant
  const updateRemainingTime = () => {
    const lockStatus = isFormLocked('login');
    setFormLock(lockStatus);
    
    if (lockStatus.locked && !lockStatus.requireCaptcha) {
      setTimeDisplay(formatRemainingTime(lockStatus.remainingTime));
    } else if (lockStatus.requireCaptcha) {
      setCaptchaRequired(true);
    }
  };

  // Vérifier le statut du verrouillage au chargement
  useEffect(() => {
    updateRemainingTime();
    
    // Configurer un intervalle pour mettre à jour le temps restant
    const checkLockStatus = () => {
      updateRemainingTime();
    };
    
    // Vérifier le statut toutes les secondes
    const timer = setInterval(checkLockStatus, 1000);
    
    return () => {
      clearInterval(timer);
    };
  }, []);

  const handlerMobileInput = (ev) => {
    let inputValue = ev.target.value.replace(/\D/g, ''); // Retirer tous les caractères non numériques
    let formattedValue = '';

    if (inputValue.length <= 10) { // Limiter à 10 chiffres pour un numéro français
      for (let i = 0; i < inputValue.length; i += 2) {
        formattedValue += inputValue.substr(i, 2);
        if (i + 2 < inputValue.length) {
          formattedValue += ' ';
        }
      }

      ev.target.value = formattedValue.trim();
    }
  }

  // Fonction de validation du numéro de téléphone
  const isValidPhoneNumber = (phoneNumber) => {
    // Supprimer tous les espaces pour vérifier uniquement les chiffres
    const digitsOnly = phoneNumber.replace(/\s/g, '');
    
    // Vérifier si c'est un numéro français valide (10 chiffres commençant par 0)
    if (digitsOnly.length !== 10) {
      setError("Format de numéro invalide");
      return false;
    }
    
    if (digitsOnly.charAt(0) !== '0') {
      setError("Format de numéro invalide");
      return false;
    }
    
    // Vérifier si ce n'est pas un numéro trop simple ou séquentiel
    if (/^0(\d)\1{8}$/.test(digitsOnly)) { // Comme 0111111111 ou 0999999999
      setError("Format de numéro invalide");
      return false;
    }
    
    if (/^0123456789$/.test(digitsOnly) || /^0987654321$/.test(digitsOnly)) {
      setError("Format de numéro invalide");
      return false;
    }
    
    return true;
  }

  const handleRecaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  // Version sécurisée avec CSRF de la récupération de l'utilisateur
  const secureGetUser = async (phoneNumber) => {
    try {
      const response = await secureAxios.post('https://connivence-salon.net/api/e-learning/users/retrieve.php', 
        new URLSearchParams({
          telephone: phoneNumber,
          lang: "fr"
        })
      );
      
      let resp = response.data.content;
      
      if (resp.length > 0) {
        localStorage.setItem("user", JSON.stringify(resp));
        return true;
      } else {
        if (localStorage.getItem("user")) {
          localStorage.removeItem("user");
        }
        return false;
      }
    } catch (error) {
      console.log("Erreur lors de la récupération de l'utilisateur:", error);
      return false;
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    
    // Forcer une mise à jour du statut de verrouillage
    updateRemainingTime();
    
    // Vérifier si le formulaire est verrouillé
    if (formLock.locked && !formLock.requireCaptcha) {
      setError(`Trop de tentatives échouées. Veuillez réessayer dans ${timeDisplay}.`);
      return;
    }
    
    // Si reCAPTCHA est requis mais non validé
    if (formLock.requireCaptcha && !captchaVerified) {
      setError("Veuillez valider le reCAPTCHA avant de continuer.");
      return;
    }
    
    const phoneNumber = mobile.current.value;
    
    // Vérifier d'abord si le format du numéro est valide
    if (!isValidPhoneNumber(phoneNumber)) {
      return; // Le message d'erreur est déjà défini dans isValidPhoneNumber
    }
    
    try {
      setLoading(true);
      
      // Utiliser notre fonction sécurisée
      const success = await secureGetUser(phoneNumber);
      
      // Récupération de l'utilisateur dans localStorage
      const storedUser = localStorage.getItem("user");
      
      if (storedUser) {
        try {
          const userObj = JSON.parse(storedUser);
          
          // Si l'utilisateur stocké est exactement [{"lessons":null}], c'est un utilisateur invalide
          if (JSON.stringify(userObj) === '[{"lessons":null}]') {
            // Utilisateur invalide - supprimer de localStorage
            localStorage.removeItem("user");
            sessionStorage.removeItem("logged");
            setError("Les informations saisies ne correspondent pas à un compte valide");
            
            // Enregistrer l'échec de tentative
            handleFailedAttempt('login');
            updateRemainingTime();
          } else if (Array.isArray(userObj) && userObj.length > 0 && userObj[0].id) {
            // Utilisateur valide avec ID - rediriger vers PIN
            sessionStorage.setItem("logged", "0");
            
            // Réinitialiser les tentatives en cas de succès
            resetAttempts('login');
            
            window.location.href = "/pin";
          } else {
            // Format inattendu - supprimer et afficher erreur
            localStorage.removeItem("user");
            sessionStorage.removeItem("logged");
            setError("Les informations saisies ne correspondent pas à un compte valide");
            
            // Enregistrer l'échec de tentative
            handleFailedAttempt('login');
            updateRemainingTime();
          }
        } catch (parseError) {
          // Erreur de parsing JSON
          localStorage.removeItem("user");
          sessionStorage.removeItem("logged");
          setError("Erreur lors de la vérification du compte");
          
          // Enregistrer l'échec de tentative
          handleFailedAttempt('login');
          updateRemainingTime();
        }
      } else {
        // Aucun utilisateur dans localStorage
        setError("Les informations saisies ne correspondent pas à un compte valide");
        
        // Enregistrer l'échec de tentative
        handleFailedAttempt('login');
        updateRemainingTime();
      }
    } catch (err) {
      setError("Échec de l'authentification, veuillez réessayer");
      
      // Enregistrer l'échec de tentative
      handleFailedAttempt('login');
      updateRemainingTime();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if(!document.body.classList.contains("fingers"))
      document.body.classList.add("fingers");
    
    // Vérifier si l'utilisateur existe dans localStorage
    try {
      const storedUser = localStorage.getItem("user");
      
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        
        if (userObj && userObj.id) {
          sessionStorage.setItem("logged", "0");
          window.location.href = "/pin";
        }
      }
    } catch (e) {
      localStorage.removeItem("user");
    }
  }, [])

  return (
    <>
      <Header />
      <main className={`login-component ${formLock.locked && !formLock.requireCaptcha ? 'form-locked' : ''}`}>
        <h1>Votre numéro de téléphone</h1>
        <form onSubmit={handleSubmit}>
          <input 
            ref={mobile} 
            className="large-input" 
            id="phone-number" 
            maxLength="14" 
            pattern="\d{2} \d{2} \d{2} \d{2} \d{2}" 
            type="text" 
            onInput={handlerMobileInput} 
            placeholder="01 23 45 67 89" 
            disabled={loading || (formLock.locked && !formLock.requireCaptcha)}
            required
          />
          
          {/* Afficher le reCAPTCHA si nécessaire directement sur la page */}
          {formLock.requireCaptcha && (
            <div className="recaptcha-container">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LdgmxorAAAAAEe_lNxk9nQ7KdpF137SXPZzr-rs" // test dev : 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI
                onChange={handleRecaptchaChange}
              />
            </div>
          )}
          
          {/* Afficher l'erreur ou le message de verrouillage */}
          {formLock.locked && !formLock.requireCaptcha ? (
            <p className="error-message lockout-error">
              Trop de tentatives échouées. Veuillez réessayer dans {timeDisplay}.
            </p>
          ) : error ? (
            <p className="error-message">{error}</p>
          ) : null}
          
          <button disabled={loading || (formLock.locked && !formLock.requireCaptcha) || (formLock.requireCaptcha && !captchaVerified)}>
            {loading ? 'Vérification...' : 'Valider'}
            {!loading && (
              <svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.0769 2L23 11.0011L14.0769 20M2 11.0034H23" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </form>
      </main>
      <Footer visible={false} />
    </>
  );
};

export default Login;